import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { updateBillingEmail } from '../services/paymentService';

const BillingEmailEditor = ({ organizationId, currentEmail, onEmailUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [email, setEmail] = useState(currentEmail || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setEmail(currentEmail || '');
  }, [currentEmail]);

  const handleSave = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await updateBillingEmail(organizationId, email);
      enqueueSnackbar('Billing email updated successfully!', { variant: 'success' });
      setIsEditing(false);
      onEmailUpdated?.(email);
    } catch (error) {
      console.error('Error updating billing email:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update billing email';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEmail(currentEmail || '');
    setIsEditing(false);
    setError('');
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Billing Email
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Billing notifications and invoices will be sent to this email address.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" alignItems="center" gap={2}>
          {isEditing ? (
            <>
              <TextField
                fullWidth
                label="Billing Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                error={!!error}
                type="email"
                placeholder="billing@company.com"
              />
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={loading}
                sx={{ minWidth: 100 }}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={loading}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {currentEmail || 'No billing email set'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
                sx={{ minWidth: 100 }}
              >
                Edit
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BillingEmailEditor; 